import { makeStyles, tokens, Text, mergeClasses } from '@fluentui/react-components';
import { RunStatusBadge } from './RunStatusBadge';
import type { Execution } from '@/types/monitoring';
import { formatDuration, formatTime } from '@/utils/dateTime';

type ExecutionItemProps = {
  execution: Execution;
  isSelected: boolean;
  onSelect: (executionId: string) => void;
};

const useStyles = makeStyles({
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXL,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  message: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase100,
  },
  statusContainer: {
    flexShrink: 0,
  },
});

export const ExecutionItem = ({ execution, isSelected, onSelect }: ExecutionItemProps) => {
  const styles = useStyles();

  const handleClick = () => {
    onSelect(execution.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(execution.id);
    }
  };

  const displayMessage = execution.inputMessage
    ? execution.inputMessage.slice(0, 50) + (execution.inputMessage.length > 50 ? '...' : '')
    : `Execution ${execution.id}`;

  return (
    <button
      className={mergeClasses(styles.item, isSelected && styles.itemSelected)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-selected={isSelected}
    >
      <div className={styles.content}>
        <Text className={styles.message}>{displayMessage}</Text>
        <Text className={styles.timestamp}>
          {formatTime(execution.timestamp)}
          {execution.duration !== undefined
            ? ` · ${formatDuration(execution.duration, { style: 'execution', fallback: '' })}`
            : ''}
        </Text>
      </div>
      <div className={styles.statusContainer}>
        <RunStatusBadge status={execution.status} size="small" />
      </div>
    </button>
  );
};
